import { useEffect, useState } from 'react';
import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './BeneficiariesInformations.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import { getReferencialData } from '@src/api/Referencial';
import { useMutation, useQuery } from '@tanstack/react-query';
import Loader from '@src/components/_shared/UI/Loader';
import Input from "@src/components/_shared/UI/Input";
import CountryDropdown from '@src/components/_shared/UI/CountryDropdownList';
import PhoneInput from '@src/components/_shared/UI/PhoneInput';
import { CountryType, SearchAddressType } from '@src/utilities/types';
import { dateToTimestamp, extractPhoneNumber, formatDate, formattedPhoneNumber, handleApiError, timestampToDate } from '@src/utilities/functions';
import { beneficiarySchema } from '@src/utilities/schemas';
import { addBeneficiaries } from '@src/api/Registration';
import Edit from '@src/assets/Edit.png';
import Garbage from '@src/assets/Garbage.png';
import AddressSearch from '@src/components/_shared/UI/AddressSearch';

export default function BeneficiariesInformations() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [currentIndex, setCurrentIndex] = useState<number>(1);
    const [countryCodeArray, setCountryCodeArray] = useState<string[]>([]);
    const [addressNotFound, setAddressNotFound] = useState<boolean>(false);
    const [displaySummary, setDisplaySummary] = useState<boolean>(false || (registration.company.beneficial_owners.length === 1 && registration.company.beneficial_owners[0]?.data.is_beneficial_owner) as boolean);
    const { data, isLoading } = useQuery({
        queryKey: ['combinedReferencialData'],
        queryFn: async () => {
            const [civilitiesData, countriesData] = await Promise.all([
                getReferencialData({ type: 'civilities' }),
                getReferencialData({ type: 'countries' })
            ]);
            return { civilitiesData, countriesData };
        }
    });

    useEffect(() => {
        if (data?.countriesData) {
            setCountryCodeArray(data.countriesData.map((obj: { phone_extension: string; }) => {return obj.phone_extension}))
        }
    }, [data?.countriesData]);

    const { mutate, isPending } = useMutation({
        mutationFn: () => addBeneficiaries({beneficiaries: registration.company.beneficial_owners, demand_id: registration.demand_id, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}), 
        onSuccess: (response) => {
            const updatedBeneficiaries = registration.company.beneficial_owners.map((beneficiary, index) => ({
                ...beneficiary,
                data: {
                    ...beneficiary.data,
                    id: response.data.beneficial_owners[index]?.id
                }
            }));
            setField('company.beneficial_owners', updatedBeneficiaries);

            setSection(4);
            setBlock('LCBFT');
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(3);
        setBlock('Beneficiaries');
    };

    const _onEditBeneficiary = (index: number) => {
        setDisplaySummary(false);
        setCurrentIndex(index);
    }

    const _onRemoveBeneficiary = (index: number) => {
        const updatedBeneficiaries = registration.company.beneficial_owners.filter((_, i) => i !== index);
        setField('company.beneficial_owners', updatedBeneficiaries);
    }

    if (!data || !data.countriesData || !data.civilitiesData) return null;

    if (isLoading) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        );
    }

    if (displaySummary) {
        return (
            <Container>
                <BackButton onClick={_onGoBack} />
                <BlockTitle title="Récapitulatif des bénéficiaires effectifs" />
                <Subtitle content="Veuillez vérifier les informations de chaque bénéficiaire effectif avant de valider." />
                {registration.company.beneficial_owners.map(({data: {person: {civility, lastname, firstname, email}, is_legal_representative, is_beneficial_owner, shareholding}}, index) => {
                    if (is_legal_representative && !is_beneficial_owner) return null;
                    return (
                        <div key={index} className={styles.summary}>
                            <div>
                                <p>{civility} {lastname} {firstname} <span>{is_legal_representative ? '(Représentant légal)' : '(Bénéficiaire effectif)'}</span></p>
                                <p>Email: <span>{email}</span></p>
                                <p>Parts: <span>{shareholding} %</span></p>
                            </div>
                            {!is_legal_representative ?
                                <div className={styles.iconsContainer}>
                                    <img src={Edit} alt="Edit" onClick={() => _onEditBeneficiary(index)} width={22} height={22} style={{ cursor: 'pointer' }} />
                                    <img src={Garbage} alt="Garbage" onClick={() => _onRemoveBeneficiary(index)} width={21} height={21} style={{ cursor: 'pointer' }} />
                                </div>
                                : null
                            }
                        </div>
                    );
                })}
                <ValidateButton title="Continuer" onClick={mutate} disabled={isPending} onLoading={isPending} />
            </Container>
        );
    }

    return (
        <Container>
            <BackButton onClick={_onGoBack} />
            <BlockTitle title={`Informations bénéficiaire ${currentIndex}`} />
            <Subtitle content="Veuillez renseigner les informations du bénéficiaire" />

            {currentIndex < registration.company.beneficial_owners.length && (data?.countriesData && countryCodeArray.length > 0) ?
                <Formik
                    key={currentIndex}
                    initialValues={{
                        shareholding: registration.company.beneficial_owners[currentIndex]?.data?.shareholding || '',
                        person: {
                            civility: registration.company.beneficial_owners[currentIndex]?.data?.person?.civility || '',
                            firstname: registration.company.beneficial_owners[currentIndex]?.data?.person?.firstname || '',
                            lastname: registration.company.beneficial_owners[currentIndex]?.data?.person?.lastname || '',
                            birthname: registration.company.beneficial_owners[currentIndex]?.data?.person?.birthname || '',
                            residence_country: registration.company.beneficial_owners[currentIndex]?.data?.person?.residence_country || '',
                            birthdate: timestampToDate(Number(registration.company.beneficial_owners[currentIndex]?.data?.person?.birthdate)) ||  '',
                            birth_country: registration.company.beneficial_owners[currentIndex]?.data?.person?.birth_country || '',
                            address: {
                                type: 'postale',
                                street_number: registration.company.beneficial_owners[currentIndex]?.data?.person?.address?.street_number || '',
                                street_extension: registration.company.beneficial_owners[currentIndex]?.data?.person?.address.street_extension || '',
                                street_name: registration.company.beneficial_owners[currentIndex]?.data?.person?.address.street_name || '',
                                street_name_further: registration.company.beneficial_owners[currentIndex]?.data?.person?.address.street_name_further || '',
                                city: {
                                    zipcode: registration.company.beneficial_owners[currentIndex]?.data?.person?.address?.city?.zipcode || '',
                                    name: registration.company.beneficial_owners[currentIndex]?.data?.person?.address?.city?.name || ''
                                },
                                country: registration.company.beneficial_owners[currentIndex]?.data?.person?.address?.country || '',
                            },
                            phone: {
                                country: data.countriesData.find((obj: any) => obj.phone_extension === extractPhoneNumber(countryCodeArray, registration.company.beneficial_owners[currentIndex]?.data?.person?.phone)?.countryCode) || { name: 'France', alpha2: 'FR', alpha3: 'FRA', numeric: 250, is_ue: true, phone_extension: '+33', rank: 0 },
                                number: extractPhoneNumber(countryCodeArray, registration.company.beneficial_owners[currentIndex]?.data?.person?.phone)?.extractPhone || '',
                            },
                            email: registration.company.beneficial_owners[currentIndex]?.data?.person?.email || ''
                        }
                    }}
                    onSubmit={(values: any) => {
                        const timestampDate = dateToTimestamp(values.person.birthdate);
                        const formattedPhone = values.person.phone.number
                            ? values.person.phone.number.startsWith('+')
                                ? values.person.phone.number
                                : `${values.person.phone.country.phone_extension}${formattedPhoneNumber(values.person.phone.country, values.person.phone.number)}`
                            : '';
                        const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
                        const result = (values.person.address.street_number || '').trim().match(regex);
                        const number = result && result[1] ? result[1] : '';
                        const extension = result && result[2] ? result[2] : '';
                        let updatedAddress = {
                            ...values.person.address,
                            ...(number && { street_number: number }),
                            ...(extension && { street_extension: extension.charAt(0).toUpperCase() }),
                            street_name: values.person.address.street_name,
                            ...(values.person.address.street_name_further && { street_name_further: values.person.address.street_name_further }),
                            city: values.person.address.city,
                            country: values.person.residence_country
                        }
                        updatedAddress = Object.fromEntries(
                            Object.entries(updatedAddress).filter(([_, value]) => value !== '' && value !== null)
                        );
                        const formattedShareholding = values.shareholding 
                            ? values.shareholding.toString().replace(',', '.') 
                            : values.shareholding;
                        const updatedValues = {
                            ...values,
                            shareholding: formattedShareholding,
                            is_legal_representative: false,
                            is_beneficial_owner: true,
                            person: {...values.person,
                                birthname: values.person.lastname,
                                phone: formattedPhone,
                                birthdate: timestampDate,
                                address: { ...updatedAddress }
                            }};
                        setField(`company.beneficial_owners.${currentIndex}.data`, updatedValues);
                        setAddressNotFound(false);
                        if (currentIndex === registration.company.beneficial_owners.length -1) {
                            setDisplaySummary(true);
                        } else {
                            setCurrentIndex(currentIndex + 1);
                        }
                    }}
                    validationSchema={beneficiarySchema}
                    enableReinitialize
                >
                    {({ handleChange, handleSubmit, values, setFieldValue, errors, touched, handleBlur, isValid }: any) => {
                        return (
                            <Form id={`beneficiaryForm-${currentIndex}`} className="registerForm" autoComplete="off">
                                <div style={{ alignSelf: 'flex-start' }}>
                                    <DropdownList
                                        title="Civilité"
                                        width="100%"
                                        options={data?.civilitiesData}
                                        selected={values.person.civility}
                                        onSelect={handleChange('person.civility')}
                                        hasError={(touched.person?.civility && errors.person?.civility) as boolean}
                                    />
                                </div>
                                <Input
                                    title="Prénom"
                                    width="100%"
                                    value={values.person.firstname}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                        handleChange('person.firstname')(capitalizedValue);
                                    }}
                                    placeholder="ex : Jean"
                                    id={`firstname-${currentIndex}`}
                                    name={`firstname-${currentIndex}`}
                                    type="text"
                                    clearInput={() => handleChange('person.firstname')('')}
                                    hasError={(touched.person?.firstname && errors.person?.firstname) as boolean}
                                    onBlur={handleBlur('person.firstname')}
                                    errorMessage={errors.person?.firstname}
                                />
                                <Input
                                    title="Nom"
                                    width="100%"
                                    value={values.person.lastname}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                        handleChange('person.lastname')(capitalizedValue);
                                    }}
                                    placeholder="ex : Dupont"
                                    id={`lastname-${currentIndex}`}
                                    name={`lastname-${currentIndex}`}
                                    type="text"
                                    hasError={(touched.person?.lastname && errors.person?.lastname) as boolean}
                                    clearInput={() => {
                                        handleChange('person.lastname')('')
                                    }}
                                    onBlur={handleBlur('person.lastname')}
                                    errorMessage={errors.person?.lastname}
                                />
                                <CountryDropdown
                                    title="Choisissez le pays de naissance"
                                    width="100%"
                                    options={data?.countriesData}
                                    selected={values.person.birth_country}
                                    onSelect={handleChange('person.birth_country')}
                                />
                                <Input
                                    title="Date de naissance"
                                    name={`birthdate-${currentIndex}`}
                                    id={`birthdate-${currentIndex}`}
                                    width="100%"
                                    type="text"
                                    placeholder="JJ/MM/AAAA"
                                    maxLength={10}
                                    value={values.person.birthdate}
                                    onChange={(e: any) => {
                                        const inputValue = e.target.value;
                                        if (e.nativeEvent.inputType === 'Backspace') {
                                            handleChange(e);
                                        } else {
                                            const formattedValue = formatDate(inputValue);
                                            handleChange({
                                                target: {
                                                    name: 'person.birthdate',
                                                    value: formattedValue,
                                                },
                                            });
                                        }
                                    }}
                                    hasError={(touched.person?.birthdate && errors.person?.birthdate) as boolean}
                                    clearInput={() => handleChange('person.birthdate')('')}
                                    onBlur={handleBlur('person.birthdate')}
                                    errorMessage={errors.person?.birthdate}
                                />
                                <CountryDropdown
                                    title="Choisissez le pays de résidence"
                                    width="100%"
                                    options={data?.countriesData}
                                    selected={values.person.residence_country}
                                    onSelect={handleChange('person.residence_country')}
                                />
                                {!addressNotFound && values.person.residence_country === 'FR' ?
                                    <div style={{ width: '100%' }}>
                                        <AddressSearch
                                            selectedAddress={values.person.address}
                                            setAddress={(searchAddress: SearchAddressType) => setFieldValue('person.address', searchAddress)}
                                            _onSelectExistingAddress={(searchAddress: SearchAddressType) => {
                                                const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
                                                const result = (searchAddress.housenumber || '').trim().match(regex);
                                                const number = result && result[1] ? result[1] : '';
                                                const extension = result && result[2] ? result[2] : '';
                                                setFieldValue('person.address', {
                                                    ...values.person.address,
                                                    street_number: number || searchAddress.housenumber,
                                                    street_extension: extension.charAt(0).toUpperCase() || '',
                                                    street_name: searchAddress.street,
                                                    city: {
                                                        ...values.person.address.city,
                                                        zipcode: searchAddress.postcode,
                                                        name: searchAddress.city,
                                                    },
                                                    country: registration.person.basic.residence_country,
                                                })
                                            }}
                                            setNotFound={setAddressNotFound}
                                        />
                                    </div>
                                    :
                                    <>
                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'}}>
                                            <div style={{width: '16%'}}>
                                                <Input
                                                    title="N°"
                                                    value={values.person.address.street_number}
                                                    onChange={handleChange('person.address.street_number')}
                                                    id="street_number"
                                                    name="street_number"
                                                    type="text"
                                                    style={{ paddingRight: 16, width: '100%', textAlign: 'center' }}
                                                />
                                            </div>
                                            <div style={{width: '81%'}}>
                                                <Input
                                                    title="Rue / voie"
                                                    value={values.person.address.street_name}
                                                    onChange={handleChange('person.address.street_name')}
                                                    placeholder="Ex : rue de la colombe"
                                                    id="street_name"
                                                    name="street_name"
                                                    type="text"
                                                    width="100%"
                                                    clearInput={() => handleChange('person.address.street_name')('')}
                                                    hasError={(touched.person?.address?.street_name && errors.person?.address?.street_name) as boolean}
                                                    onBlur={handleBlur('person.address.street_name')}
                                                    errorMessage={errors.person?.address?.street_name}
                                                />
                                            </div>
                                        </div>
                                        <Input
                                            title="Complément d’adresse (optionnel)"
                                            value={values.person.address.street_name_further}
                                            onChange={handleChange('person.address.street_name_further')}
                                            placeholder=""
                                            id="street_name_further"
                                            name="street_name_further"
                                            type="text"
                                            width="100%"
                                            clearInput={() => handleChange('person.address.street_name_further')('')}
                                        />
                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'}}>
                                            <div style={{ width: '30%' }}>
                                                <Input
                                                    title="Code postal"
                                                    value={values.person.address.city.zipcode}
                                                    onChange={handleChange('person.address.city.zipcode')}
                                                    placeholder="Ex : 75001"
                                                    id="zipcode"
                                                    name="zipcode"
                                                    type="text"
                                                    width="100%"
                                                    hasError={(touched.person?.address?.city?.zipcode && errors.person?.address?.city?.zipcode) as boolean}
                                                    onBlur={handleBlur('person.address.city.zipcode')}
                                                />
                                            </div>
                                            <div style={{ width: '67%' }}>
                                                <Input
                                                    title="Ville"
                                                    value={values.person.address.city.name}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                                        handleChange('person.address.city.name')(capitalizedValue);
                                                    }}
                                                    placeholder="Ex : Paris"
                                                    id="cityname"
                                                    name="cityname"
                                                    type="text"
                                                    width="100%"
                                                    clearInput={() => handleChange('person.address.city.name')('')}
                                                    hasError={(touched.person?.address?.city?.name && errors.person?.address?.city?.name) as boolean}
                                                    onBlur={handleBlur('person.address.city.name')}
                                                    errorMessage={errors.person?.address?.city?.name}
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                                <PhoneInput
                                    title="Numéro de téléphone"
                                    width="100%"
                                    countries={data?.countriesData}
                                    value={values.person.phone.number}
                                    onChange={handleChange('person.phone.number')}
                                    placeholder="Numéro de téléphone"
                                    clearInput={() => handleChange('person.phone.number')('')}
                                    hasError={(values.person?.phone?.number && errors.person?.phone?.number) as boolean}
                                    onCountryChange={(country: CountryType) => setFieldValue('person.phone.country', country)}
                                    selectedCountry={values.person.phone.country}
                                    errorMessage={errors.person?.phone?.number}
                                />
                                <Input
                                    title="Saisissez l’adresse email"
                                    value={values.person.email}
                                    onChange={handleChange('person.email')}
                                    placeholder="Email"
                                    id={`email-${currentIndex}`}
                                    name={`email-${currentIndex}`}
                                    type="email"
                                    width="100%"
                                    hasError={(touched.person?.email && errors.person?.email) as boolean}
                                    clearInput={() => handleChange('person.email')('')}
                                    onBlur={handleBlur('person.email')}
                                    errorMessage={errors.person?.email}
                                />
                                <Input
                                    title="Pourcentage de détention du capital de la société"
                                    name={`shareholding-${currentIndex}`}
                                    id={`shareholding-${currentIndex}`}
                                    width="100%"
                                    placeholder="Ex : 50"
                                    type="number"
                                    value={values.shareholding}
                                    hasError={(touched.shareholding && errors.shareholding) as boolean}
                                    onChange={handleChange('shareholding')}
                                    clearInput={() => handleChange('shareholding')('')}
                                    onBlur={handleBlur('shareholding')}
                                    errorMessage={errors.shareholding}
                                />
                                <ValidateButton
                                    title="Continuer"
                                    disabled={!isValid}
                                    onClick={handleSubmit}
                                />
                            </Form>
                        )}}
                </Formik>
                : null}
        </Container>
    );
}
